import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import memberService from '../../services/memberService';
import minorMemberService from '../../services/minorMemberService';
import servePositionService from '../../services/servePositionService';
import organizationService from '../../services/organizationService';
import { addNotification } from '../../store/slices/uiSlice';
import { getErrorMessage } from '../../utils/errorMessage';
import './MemberForm.css';
import '../MinorMembers/MinorMemberForm.css';
import { useTranslation } from 'react-i18next';

const composeAddress = (data) => {
  const parts = [
    data.new_region,
    data.addr_district_name,
    data.street,
    data.estate,
    data.building,
    data.new_number,
  ].map((s) => (s || '').trim()).filter(Boolean);
  return parts.join(' ');
};

// ── Family member editing (shared with MinorMemberForm) ───────────────────────
const ADULT_FAMILY_RELATION_GROUPS = [
  { label: '祖父母輩', options: ['祖父', '祖母', '外祖父', '外祖母'] },
  { label: '父母輩',   options: ['父親', '母親', '繼父', '繼母', '養父', '養母', '監護人',
                                  '伯父', '伯母', '叔父', '叔母', '舅父', '舅母', '姑父', '姑母', '姨父', '姨母'] },
  { label: '配偶',     options: ['丈夫', '妻子'] },
  { label: '同輩',     options: ['兄', '弟', '姊', '妹', '堂兄', '堂弟', '堂姊', '堂妹', '表兄', '表弟', '表姊', '表妹'] },
  { label: '子女',     options: ['兒子', '女兒'] },
];

const FM_TYPE_LABEL = { regular: '成人', associate: '成人', temporary: '成人', adult: '成人', children: '兒童', teenage: '少年' };
const FM_TYPE_COLOR = { regular: '#059669', associate: '#059669', temporary: '#059669', adult: '#059669', children: '#0ea5e9', teenage: '#7c3aed' };
const EMPTY_FAMILY_ROW = { relation: '兒子', chi_name: '', is_church_member: false, worship_code: '' };

// ── Serve Positions Inline Editor ─────────────────────────────────────────────
const ServePositionsInlineEditor = ({ memberId }) => {
  const dispatch = useDispatch();
  const [assignments, setAssignments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [posRes, asgRes] = await Promise.all([
          servePositionService.getPositions({ limit: 1000, is_active: true }),
          servePositionService.getAssignments({ member_id: memberId, status: 'active', limit: 100 }),
        ]);
        if (!cancelled) {
          setPositions(posRes.items || []);
          setAssignments(asgRes.items || []);
        }
      } catch {}
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [memberId]);

  const handleAdd = async () => {
    if (!selectedId || saving) return;
    setSaving(true);
    try {
      await servePositionService.createAssignment({
        member_id: memberId,
        position_id: parseInt(selectedId),
        status: 'active',
      });
      const res = await servePositionService.getAssignments({ member_id: memberId, status: 'active', limit: 100 });
      setAssignments(res.items || []);
      setSelectedId('');
      dispatch(addNotification({ type: 'success', message: '已新增事奉崗位' }));
    } catch {
      dispatch(addNotification({ type: 'error', message: '新增崗位失敗' }));
    }
    setSaving(false);
  };

  const handleRemove = async (assignmentId) => {
    if (saving) return;
    setSaving(true);
    try {
      await servePositionService.deleteAssignment(assignmentId);
      setAssignments(prev => prev.filter(a => a.id !== assignmentId));
      dispatch(addNotification({ type: 'success', message: '已移除事奉崗位' }));
    } catch {
      dispatch(addNotification({ type: 'error', message: '移除崗位失敗' }));
    }
    setSaving(false);
  };

  const assigned = new Set(assignments.map(a => a.position_id));
  const available = positions.filter(p => !assigned.has(p.id));

  if (loading) return <span style={{ color: '#9ca3af', fontSize: 13 }}>載入中...</span>;

  return (
    <div className="serve-positions-editor">
      <div className="serve-positions-chips">
        {assignments.length === 0 && (
          <span style={{ color: '#9ca3af', fontSize: 13 }}>尚未指定事奉崗位</span>
        )}
        {assignments.map(a => (
          <span key={a.id} className="position-chip">
            {a.position_name}
            <button
              type="button"
              className="chip-remove"
              onClick={() => handleRemove(a.id)}
              disabled={saving}
              title="移除"
            >×</button>
          </span>
        ))}
      </div>
      {available.length > 0 && (
        <div className="serve-add-row">
          <select
            className="form-control form-control-sm"
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            style={{ maxWidth: 220 }}
          >
            <option value="">-- 選擇崗位新增 --</option>
            {available.map(p => (
              <option key={p.id} value={p.id}>{p.position_name}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleAdd}
            disabled={!selectedId || saving}
          >
            <i className="fas fa-plus"></i> 新增
          </button>
        </div>
      )}
    </div>
  );
};

const FamilyMemberRow = ({ f, idx, onUpdate, onRemove }) => {
  const [query, setQuery]             = useState(f.worship_code || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop]       = useState(false);
  const [searching, setSearching]     = useState(false);
  const debounceRef  = useRef(null);
  const blurTimerRef = useRef(null);

  useEffect(() => { if (!f.is_church_member) setQuery(''); }, [f.is_church_member]);

  const runSearch = useCallback(async (q) => {
    if (!q.trim()) { setSuggestions([]); setShowDrop(false); return; }
    setSearching(true);
    try {
      const [adults, minors] = await Promise.all([
        memberService.searchMembers(q, 8),
        minorMemberService.searchMembers(q, 8),
      ]);
      const adultItems = (adults || []).map(m => ({ worship_code: m.worship_id, chi_name: m.chi_name, member_type: 'adult', label: `${m.worship_id} · ${m.chi_name}` }));
      const minorItems = (minors || []).map(m => ({ worship_code: m.worship_id, chi_name: m.chi_name, member_type: m.member_type, label: `${m.worship_id} · ${m.chi_name}` }));
      setSuggestions([...adultItems, ...minorItems]);
      setShowDrop(true);
    } catch { setSuggestions([]); }
    finally { setSearching(false); }
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onUpdate(idx, 'worship_code', val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val), 300);
  };

  const handleSelect = (item) => {
    setQuery(item.worship_code);
    setSuggestions([]);
    setShowDrop(false);
    onUpdate(idx, 'worship_code', item.worship_code);
    onUpdate(idx, 'chi_name', item.chi_name || '');
  };

  const handleFocus = () => { clearTimeout(blurTimerRef.current); if (suggestions.length > 0) setShowDrop(true); };
  const handleBlur  = () => { blurTimerRef.current = setTimeout(() => setShowDrop(false), 200); };

  return (
    <tr>
      <td>
        <select value={f.relation} onChange={e => onUpdate(idx, 'relation', e.target.value)} className="form-control form-control-sm">
          {ADULT_FAMILY_RELATION_GROUPS.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map(r => <option key={r} value={r}>{r}</option>)}
            </optgroup>
          ))}
        </select>
      </td>
      <td>
        <input type="text" value={f.chi_name || ''} onChange={e => onUpdate(idx, 'chi_name', e.target.value)}
          className="form-control form-control-sm" placeholder="姓名" />
      </td>
      <td className="text-center" style={{ verticalAlign: 'middle' }}>
        <input type="checkbox" checked={f.is_church_member}
          onChange={e => {
            onUpdate(idx, 'is_church_member', e.target.checked);
            if (!e.target.checked) { setQuery(''); setSuggestions([]); onUpdate(idx, 'worship_code', ''); }
          }}
        />
      </td>
      <td style={{ position: 'relative', minWidth: '180px' }}>
        {f.is_church_member && (
          <>
            <input type="text" value={query} onChange={handleQueryChange} onFocus={handleFocus} onBlur={handleBlur}
              className="form-control form-control-sm" placeholder="搜尋姓名 / 敬拜號" autoComplete="off" />
            {searching && <div className="fam-searching">搜尋中...</div>}
            {showDrop && suggestions.length > 0 && (
              <ul className="fam-suggestions">
                {suggestions.map((s, si) => (
                  <li key={si} className="fam-suggestion-item" onMouseDown={e => { e.preventDefault(); handleSelect(s); }}>
                    <span className="fam-type-badge" style={{ background: FM_TYPE_COLOR[s.member_type] || '#6b7280' }}>{FM_TYPE_LABEL[s.member_type] || s.member_type}</span>
                    <span className="fam-code">{s.worship_code}</span>
                    <span className="fam-name">{s.chi_name}</span>
                  </li>
                ))}
              </ul>
            )}
            {showDrop && !searching && suggestions.length === 0 && query.trim() && (
              <div className="fam-no-results">找不到相關會員</div>
            )}
          </>
        )}
      </td>
      <td style={{ verticalAlign: 'middle' }}>
        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(idx)}>
          <i className="fas fa-times"></i>
        </button>
      </td>
    </tr>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const MemberForm = ({ mode = 'create' }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = mode === 'edit' || id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [memberData, setMemberData] = useState(null); // Store original member data including member_type
  const [showHKIDPassword, setShowHKIDPassword] = useState(false); // Toggle HKID visibility in form
  
  // Upgrade modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeData, setUpgradeData] = useState({
    member_type: 'associate',
    worship_id: ''
  });
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  const [groupOptions, setGroupOptions] = useState([]);
  const [generatingWorshipId, setGeneratingWorshipId] = useState(false);

  const [formData, setFormData] = useState({
    // Basic fields
    worship_id: '',
    joomla_member_id: '',
    octopus_card_number: '',
    chi_name: '',
    eng_name: '',
    nickname: '',
    sex_name: '',
    date_of_birth: '',
    hkid: '',
    native_place: '',
    
    // Contact fields
    mobile: '',
    email: '',
    tel_day: '',
    tel_night: '',
    company_tel: '',
    fax: '',
    
    // Address fields
    addr2: '',
    street: '',
    estate: '',
    building: '',
    new_number: '',
    addr_district_name: '',
    new_region: '',
    
    // Postal address fields
    postal_addr2: '',
    street_postal: '',
    estate_postal: '',
    building_postal: '',
    number_postal: '',
    
    // Membership fields
    status_name: 'active',
    date_of_join: new Date().toISOString().split('T')[0],
    date_of_join_add: '',
    date_of_transfer_add: '',
    
    // Education fields
    edu_grade_name: '',
    edu_name: '',
    
    // Family fields
    married_status_name: '',
    date_of_married: '',
    child_qty: 0,
    
    // Occupation fields
    job: '',
    income_name: '',
    work_region_name: '',
    pro_qual_items: '',
    
    // Baptism fields
    date_of_baptism: '',
    baptism_church: '',
    baptism_no: '',
    baptist_priest: '',
    baptism_appointed_date: '',
    
    // Religious background
    religion_name: '',
    ever_church: '',
    date_of_believe_god: '',
    
    // Service fields
    serve_post_items: '',
    serve_post_exp: '',
    
    // Organization fields
    groups: '',
    zones: '',
    districts: '',
    cell_group_name4: '',
    org_leadership: '',
    
    // Health fields
    health: '',
    health_items_name: '',
    
    // Emergency contact fields
    emergency_contact_person: '',
    emergency_contact_tel: '',
    emergency_contact_relation_name: '',
    
    // Communication fields
    is_receive_info: '',
    
    // Remarks
    remark: '',
    remark2: ''
  });

  // Fetch member data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      fetchMemberData();
    }
  }, [id, isEditMode]);

  // Load org tree for group → zone/district autofill
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tree = await organizationService.getOrganizationTree();
        const options = [];
        (tree || []).forEach((district) => {
          (district.zones || []).forEach((zone) => {
            (zone.groups || []).forEach((group) => {
              options.push({
                id: group.id,
                name: group.name,
                zoneName: zone.name,
                districtName: district.name,
              });
            });
          });
        });
        if (!cancelled) setGroupOptions(options);
      } catch (err) {
        console.error('Failed to load organization tree', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchMemberData = async () => {
    setLoading(true);
    setError(null);
    try {
      const member = await memberService.getMemberById(id);
      console.log('Fetched member data:', member);
      
      if (!member) {
        throw new Error(t('member.detail.notFound'));
      }
      
      // Store original member data
      setMemberData(member);
      
      // Convert datetime fields to date format for input[type="date"]
      // Handle null/undefined values properly
      const formattedData = {
        // Basic fields
        worship_id: member.worship_id || '',
        joomla_member_id: member.joomla_member_id != null ? String(member.joomla_member_id) : '',
        octopus_card_number: member.octopus_card_number || '',
        chi_name: member.chi_name || '',
        eng_name: member.eng_name || '',
        nickname: member.nickname || '',
        sex_name: member.sex_name || '',
        date_of_birth: member.date_of_birth ? new Date(member.date_of_birth).toISOString().split('T')[0] : '',
        hkid: '', // Don't populate HKID in edit mode for security - show placeholder instead
        native_place: member.native_place || '',
        
        // Contact fields
        mobile: member.mobile || '',
        email: member.email || '',
        tel_day: member.tel_day || '',
        tel_night: member.tel_night || '',
        company_tel: member.company_tel || '',
        fax: member.fax || '',
        
        // Address fields
        addr2: member.addr2 || '',
        street: member.street || '',
        estate: member.estate || '',
        building: member.building || '',
        new_number: member.new_number || '',
        addr_district_name: member.addr_district_name || '',
        new_region: member.new_region || '',
        
        // Postal address fields
        postal_addr2: member.postal_addr2 || '',
        street_postal: member.street_postal || '',
        estate_postal: member.estate_postal || '',
        building_postal: member.building_postal || '',
        number_postal: member.number_postal || '',
        
        // Membership fields
        status_name: member.status_name || 'active',
        date_of_join: member.date_of_join ? new Date(member.date_of_join).toISOString().split('T')[0] : '',
        date_of_join_add: member.date_of_join_add ? new Date(member.date_of_join_add).toISOString().split('T')[0] : '',
        date_of_transfer_add: member.date_of_transfer_add ? new Date(member.date_of_transfer_add).toISOString().split('T')[0] : '',
        
        // Education fields
        edu_grade_name: member.edu_grade_name || '',
        edu_name: member.edu_name || '',
        
        // Family fields
        married_status_name: member.married_status_name || '',
        date_of_married: member.date_of_married ? new Date(member.date_of_married).toISOString().split('T')[0] : '',
        child_qty: member.child_qty || 0,
        
        // Occupation fields
        job: member.job || '',
        income_name: member.income_name || '',
        work_region_name: member.work_region_name || '',
        pro_qual_items: member.pro_qual_items || '',
        
        // Baptism fields
        date_of_baptism: member.date_of_baptism ? new Date(member.date_of_baptism).toISOString().split('T')[0] : '',
        baptism_church: member.baptism_church || '',
        baptism_no: member.baptism_no || '',
        baptist_priest: member.baptist_priest || '',
        baptism_appointed_date: member.baptism_appointed_date || '',
        
        // Religious background
        religion_name: member.religion_name || '',
        ever_church: member.ever_church || '',
        date_of_believe_god: member.date_of_believe_god ? new Date(member.date_of_believe_god).toISOString().split('T')[0] : '',
        
        // Service fields
        serve_post_items: member.serve_post_items || '',
        serve_post_exp: member.serve_post_exp || '',
        
        // Organization fields
        // 區長: 小組=NA; 區牧: 小組+分區=NA (API already clears these)
        groups: member.org_leadership === 'zone_leader' || member.org_leadership === 'district_pastor'
          ? ''
          : (member.groups || member.cell_group_name || ''),
        zones: member.org_leadership === 'district_pastor'
          ? ''
          : (member.zones || member.cell_group_name2 || ''),
        districts: member.districts || member.cell_group_name3 || '',
        cell_group_name4: member.cell_group_name4 || '',
        org_leadership: member.org_leadership || '',
        
        // Health fields
        health: member.health || '',
        health_items_name: member.health_items_name || '',
        
        // Emergency contact fields
        emergency_contact_person: member.emergency_contact_person || '',
        emergency_contact_tel: member.emergency_contact_tel || '',
        emergency_contact_relation_name: member.emergency_contact_relation_name || '',
        
        // Communication fields
        is_receive_info: member.is_receive_info || '',
        
        // Remarks
        remark: member.remark || '',
        remark2: member.remark2 || ''
      };
      
      console.log('Formatted data:', formattedData);
      setFormData(formattedData);
      setFamilyMembers(member.family_members || []);
    } catch (err) {
      let errorMsg = t('member.form.errors.load');
      const detail = err.response?.data?.detail;
      
      if (detail) {
        if (Array.isArray(detail)) {
          errorMsg = detail.map(e => {
            const field = e.loc ? e.loc.slice(1).join('.') : '';
            return field ? `${field}: ${e.msg}` : e.msg;
          }).join('; ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object' && detail.msg) {
          errorMsg = detail.msg;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      console.error('Error fetching member:', err);
      console.error('Error details:', err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'groups') {
      const matched = groupOptions.find((g) => g.name === value);
      setFormData((prev) => ({
        ...prev,
        groups: value,
        zones: matched ? matched.zoneName : (value ? prev.zones : ''),
        districts: matched ? matched.districtName : (value ? prev.districts : ''),
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerateWorshipId = async () => {
    setGeneratingWorshipId(true);
    try {
      const memberType = memberData?.member_type || 'regular';
      const res = await memberService.getNextWorshipId(memberType);
      setFormData((prev) => ({
        ...prev,
        worship_id: res.next_worship_id || '',
      }));
    } catch (err) {
      console.error('Failed to generate worship ID', err);
      dispatch(addNotification({
        type: 'error',
        message: t('member.form.errors.save', { defaultValue: 'Failed to generate worship ID' }),
      }));
    } finally {
      setGeneratingWorshipId(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Client-side validation for required fields
    if (!formData.chi_name || formData.chi_name.trim() === '') {
      setError(t('member.form.errors.chiNameRequired', { defaultValue: 'Chinese name is required' }));
      setSubmitting(false);
      return;
    }

    if (!formData.mobile || formData.mobile.trim() === '') {
      setError(t('member.form.errors.mobileRequired', { defaultValue: 'Mobile number is required' }));
      setSubmitting(false);
      return;
    }

    try {
      // Helper function to convert date string to ISO format or null
      const toDateTimeOrNull = (dateStr) => {
        if (!dateStr || dateStr.trim() === '') return null;
        try {
          return new Date(dateStr).toISOString();
        } catch {
          return null;
        }
      };

      // Prepare data: convert date strings to ISO format for datetime fields
      const submitData = {
        ...formData,
        // All datetime fields need to be converted from empty string to null
        date_of_birth: toDateTimeOrNull(formData.date_of_birth),
        date_of_join: toDateTimeOrNull(formData.date_of_join),
        date_of_join_add: toDateTimeOrNull(formData.date_of_join_add),
        date_of_transfer_add: toDateTimeOrNull(formData.date_of_transfer_add),
        date_of_baptism: toDateTimeOrNull(formData.date_of_baptism),
        date_of_married: toDateTimeOrNull(formData.date_of_married),
        date_of_believe_god: toDateTimeOrNull(formData.date_of_believe_god),
        date_of_pass_away: toDateTimeOrNull(formData.date_of_pass_away),
        // Convert integer fields - empty string to null, otherwise parse as int
        child_qty: formData.child_qty === '' || formData.child_qty === null ? null : parseInt(formData.child_qty, 10),
        // Convert empty strings to null for optional string fields to avoid Pydantic issues
        worship_id: formData.worship_id || null,
        joomla_member_id: formData.joomla_member_id === '' || formData.joomla_member_id == null
          ? null
          : parseInt(formData.joomla_member_id, 10),
        card_id: formData.card_id || null,
        octopus_card_number: formData.octopus_card_number || null,
        eng_name: formData.eng_name || null,
        nickname: formData.nickname || null,
        dedicate_name: formData.dedicate_name || null,
        sex_name: formData.sex_name || null,
        hkid: formData.hkid || null,
        native_place: formData.native_place || null,
        tel_day: formData.tel_day || null,
        tel_night: formData.tel_night || null,
        fax: formData.fax || null,
        company_tel: formData.company_tel || null,
        mobile: formData.mobile || null,
        email: formData.email || null,
        addr2: composeAddress(formData) || formData.addr2 || null,
        new_region: formData.new_region || null,
        addr_district_name: formData.addr_district_name || null,
        street: formData.street || null,
        estate: formData.estate || null,
        building: formData.building || null,
        new_number: formData.new_number || null,
        postal_addr2: formData.postal_addr2 || null,
        street_postal: formData.street_postal || null,
        estate_postal: formData.estate_postal || null,
        building_postal: formData.building_postal || null,
        number_postal: formData.number_postal || null,
        edu_grade_name: formData.edu_grade_name || null,
        edu_name: formData.edu_name || null,
        married_status_name: formData.married_status_name || null,
        job: formData.job || null,
        income_name: formData.income_name || null,
        work_region_name: formData.work_region_name || null,
        pro_qual_items: formData.pro_qual_items || null,
        baptism_church: formData.baptism_church || null,
        baptism_no: formData.baptism_no || null,
        baptist_priest: formData.baptist_priest || null,
        baptism_appointed_date: formData.baptism_appointed_date || null,
        religion_name: formData.religion_name || null,
        ever_church: formData.ever_church || null,
        serve_post_items: formData.serve_post_items || null,
        serve_post_exp: formData.serve_post_exp || null,
        groups: formData.org_leadership === 'zone_leader' || formData.org_leadership === 'district_pastor'
          ? null
          : (formData.groups || null),
        zones: formData.org_leadership === 'district_pastor'
          ? null
          : (formData.zones || null),
        districts: formData.districts || null,
        cell_group_name4: formData.cell_group_name4 || null,
        health: formData.health || null,
        health_items_name: formData.health_items_name || null,
        emergency_contact_person: formData.emergency_contact_person || null,
        emergency_contact_tel: formData.emergency_contact_tel || null,
        emergency_contact_relation_name: formData.emergency_contact_relation_name || null,
        is_receive_info: formData.is_receive_info || null,
        remark: formData.remark || null,
        remark2: formData.remark2 || null,
        family_members: familyMembers.length > 0 ? familyMembers : null,
      };

      if (isEditMode) {
        await memberService.updateMember(id, submitData);
        dispatch(addNotification({
          type: 'success',
          title: t('common.successTitle'),
          message: t('member.form.successUpdate')
        }));
      } else {
        await memberService.createMember(submitData);
        dispatch(addNotification({
          type: 'success',
          title: t('common.successTitle'),
          message: t('member.form.successCreate')
        }));
      }
      
      navigate('/members');
    } catch (err) {
      let errorMessage = t('member.form.errors.save');
      const detail = err.response?.data?.detail;
      
      if (detail) {
        if (Array.isArray(detail)) {
          // Handle Pydantic validation errors array
          errorMessage = detail.map(e => {
            const field = e.loc ? e.loc.slice(1).join('.') : '';
            return field ? `${field}: ${e.msg}` : e.msg;
          }).join('; ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      }
      
      setError(errorMessage);
      console.error('Error saving member:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm(t('member.form.cancelConfirm'))) {
      navigate('/members');
    }
  };

  // Upgrade handlers
  const handleUpgradeClick = () => {
    setUpgradeData({
      member_type: memberData?.member_type || 'regular',
      worship_id: formData.worship_id || ''
    });
    setShowUpgradeModal(true);
  };

  const handleUpgradeChange = (e) => {
    const { name, value } = e.target;
    setUpgradeData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpgradeMember = async () => {
    // Require worship_id for non-temporary member types
    if (upgradeData.member_type !== 'temporary' && !upgradeData.worship_id.trim()) {
      dispatch(addNotification({ 
        type: 'error', 
        title: t('common.errorTitle'), 
        message: t('member.upgrade.worshipIdRequired', { defaultValue: 'Worship ID is required for this member type' })
      }));
      return;
    }

    try {
      setUpgradeLoading(true);
      
      await api.post('/api/v1/members/admin/upgrade', {
        member_id: parseInt(id),
        member_type: upgradeData.member_type,
        worship_id: upgradeData.worship_id || null
      });

      dispatch(addNotification({ 
        type: 'success', 
        title: t('common.successTitle'), 
        message: t('member.upgrade.success', { name: formData.chi_name, defaultValue: `${formData.chi_name} has been upgraded successfully!` })
      }));
      
      setShowUpgradeModal(false);
      
      // Refresh member data
      fetchMemberData();
    } catch (err) {
      let errorMsg = t('member.upgrade.error', { defaultValue: 'Failed to upgrade member' });
      const detail = err.response?.data?.detail;
      
      if (detail) {
        if (Array.isArray(detail)) {
          errorMsg = detail.map(e => {
            const field = e.loc ? e.loc.slice(1).join('.') : '';
            return field ? `${field}: ${e.msg}` : e.msg;
          }).join('; ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (typeof detail === 'object' && detail.msg) {
          errorMsg = detail.msg;
        }
      }
      
      dispatch(addNotification({ 
        type: 'error', 
        title: t('common.errorTitle'), 
        message: errorMsg
      }));
    } finally {
      setUpgradeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="member-form-container">
        <div className="loading-container">
          <div className="spinner-border" role="status">
            <span className="sr-only">{t('loading.loading')}</span>
          </div>
          <p>{t('member.detail.loadingData')}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'basic',    label: '基本資料',  icon: 'fas fa-id-card' },
    { id: 'contact',  label: '聯絡/住址', icon: 'fas fa-phone' },
    { id: 'church',   label: '會籍/事奉', icon: 'fas fa-church' },
    { id: 'family',   label: '家庭狀況',  icon: 'fas fa-users' },
    { id: 'advanced', label: '進階資料',  icon: 'fas fa-list-alt' },
    { id: 'remarks',  label: '備註',     icon: 'fas fa-sticky-note' },
  ];

  return (
    <div className="minor-member-form-container">
      <div className="form-header">
        <h2>
          <i className={`fas ${isEditMode ? 'fa-edit' : 'fa-user-plus'} mr-2`}></i>
          {isEditMode
            ? t('member.form.editTitle', {
                name: (formData.nickname || '').trim() || (formData.chi_name || '').trim() || '…',
              })
            : t('member.form.createTitle')}
        </h2>
        <button type="button" className="btn btn-secondary" onClick={handleCancel}>
          <i className="fas fa-arrow-left"></i> 返回
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="member-form">
        {/* Tab navigation */}
        <div className="form-tabs">
          {tabs.map(tb => (
            <button
              key={tb.id}
              type="button"
              className={`form-tab ${activeTab === tb.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tb.id)}
            >
              <i className={tb.icon}></i> {tb.label}
              {tb.id === 'family' && familyMembers.length > 0 && (
                <span style={{ marginLeft: 5, background: '#2563eb', color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '11px' }}>{familyMembers.length}</span>
              )}
            </button>
          ))}
        </div>

        <div className="form-body">

        {/* ── 基本資料 ─────────────────────────── */}
        {activeTab === 'basic' && (
        <div className="form-section">
          <h3 className="section-title">{t('member.form.sections.basic')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="worship_id">{t('member.form.labels.worshipId')}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  id="worship_id"
                  name="worship_id"
                  className="form-control"
                  value={formData.worship_id}
                  onChange={handleChange}
                  placeholder="000001 / T000001"
                  maxLength={7}
                  pattern="^\d{6}$|^T\d{6}$"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleGenerateWorshipId}
                  disabled={generatingWorshipId}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {generatingWorshipId ? '…' : t('member.form.labels.autoWorshipId')}
                </button>
              </div>
              <small className="form-text text-muted">
                {t('member.form.placeholders.statusFormat')}
              </small>
              {/* Hidden mapping to Joomla user id (Route-1 auth) */}
              <input
                type="hidden"
                id="joomla_member_id"
                name="joomla_member_id"
                value={formData.joomla_member_id || ''}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="status_name">{t('member.form.labels.status')}</label>
              <select
                id="status_name"
                name="status_name"
                className="form-control"
                value={formData.status_name}
                onChange={handleChange}
                required
              >
                <option value="active">{t('member.form.labels.statusActive')}</option>
                <option value="inactive">{t('member.form.labels.statusInactive')}</option>
                <option value="suspended">{t('member.form.labels.statusSuspended')}</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="chi_name">{t('member.form.labels.chiName')}</label>
              <input
                type="text"
                id="chi_name"
                name="chi_name"
                className="form-control"
                value={formData.chi_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="eng_name">{t('member.form.labels.engName')}</label>
              <input
                type="text"
                id="eng_name"
                name="eng_name"
                className="form-control"
                value={formData.eng_name}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="nickname">{t('member.form.labels.nickname')}</label>
              <input
                type="text"
                id="nickname"
                name="nickname"
                className="form-control"
                value={formData.nickname}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sex_name">{t('member.form.labels.gender')}</label>
              <select
                id="sex_name"
                name="sex_name"
                className="form-control"
                value={formData.sex_name}
                onChange={handleChange}
              >
                <option value="">{t('member.form.options.genderSelect')}</option>
                <option value="Male">{t('member.form.options.genderMale')}</option>
                <option value="Female">{t('member.form.options.genderFemale')}</option>
                <option value="Other">{t('member.form.options.genderOther')}</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="date_of_birth">{t('member.form.labels.dob')}</label>
              <input
                type="date"
                id="date_of_birth"
                name="date_of_birth"
                className="form-control"
                value={formData.date_of_birth}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="hkid">{t('member.form.labels.hkid')}</label>
              <input
                type="text"
                id="hkid"
                name="hkid"
                className="form-control"
                value={formData.hkid}
                onChange={handleChange}
                placeholder={memberData && memberData.display_hkid ? `Current: ${memberData.display_hkid}` : ''}
              />
              {memberData && memberData.display_hkid && (
                <small className="form-text text-muted">
                  {t('member.form.hkidEncryptionNote', 'Leave blank to keep current HKID. Enter new value to update.')}
                </small>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="octopus_card_number">{t('member.form.labels.octopusCardNumber')}</label>
              <input
                type="text"
                id="octopus_card_number"
                name="octopus_card_number"
                className="form-control"
                value={formData.octopus_card_number}
                onChange={handleChange}
                placeholder={t('member.form.placeholders.octopusCardNumber')}
              />
            </div>
          </div>
        </div>
        )}

        {/* ── 聯絡/住址 ────────────────────────── */}
        {activeTab === 'contact' && (
        <div className="form-section">
          <h3 className="section-title">{t('member.form.sections.contact')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="mobile">{t('member.form.labels.mobile')} *</label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                className="form-control"
                value={formData.mobile}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">{t('member.form.labels.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
          </div>

          <h3 className="section-title" style={{ marginTop: '1.5rem' }}>{t('member.form.sections.address')}</h3>
          <p className="form-text text-muted" style={{ marginTop: 0 }}>{t('member.form.hints.addressStructured')}</p>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="new_region_ct">{t('member.form.labels.region')}</label>
              <input type="text" id="new_region_ct" name="new_region" className="form-control" value={formData.new_region} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="addr_district_name_ct">{t('member.form.labels.addrDistrict')}</label>
              <input type="text" id="addr_district_name_ct" name="addr_district_name" className="form-control" value={formData.addr_district_name} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="street_ct">{t('member.form.labels.street')}</label>
              <input type="text" id="street_ct" name="street" className="form-control" value={formData.street} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="estate_ct">{t('member.form.labels.estate')}</label>
              <input type="text" id="estate_ct" name="estate" className="form-control" value={formData.estate} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="building_ct">{t('member.form.labels.building')}</label>
              <input type="text" id="building_ct" name="building" className="form-control" value={formData.building} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="new_number_ct">{t('member.form.labels.unitNumber')}</label>
              <input type="text" id="new_number_ct" name="new_number" className="form-control" value={formData.new_number} onChange={handleChange} />
            </div>
          </div>
          {composeAddress(formData) && (
            <div className="form-group">
              <label>{t('member.form.labels.addr')}</label>
              <div className="form-control" style={{ background: '#f9fafb', minHeight: 38 }}>{composeAddress(formData)}</div>
            </div>
          )}
          <h3 className="section-title" style={{ marginTop: '1.5rem' }}>{t('member.form.sections.otherPhones')}</h3>
          <p className="form-text text-muted" style={{ marginTop: 0 }}>{t('member.form.hints.otherPhones')}</p>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="tel_day_ct">{t('member.form.labels.telDay')}</label>
              <input type="tel" id="tel_day_ct" name="tel_day" className="form-control" value={formData.tel_day} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="tel_night_ct">{t('member.form.labels.telNight')}</label>
              <input type="tel" id="tel_night_ct" name="tel_night" className="form-control" value={formData.tel_night} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="company_tel_ct">{t('member.form.labels.companyTel')}</label>
              <input type="tel" id="company_tel_ct" name="company_tel" className="form-control" value={formData.company_tel} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="fax_ct">{t('member.form.labels.fax')}</label>
              <input type="tel" id="fax_ct" name="fax" className="form-control" value={formData.fax} onChange={handleChange} />
            </div>
          </div>
        </div>
        )}

        {/* ── 會籍/事奉 ────────────────────────── */}
        {activeTab === 'church' && (
        <div className="form-section">
          <h3 className="section-title">{t('member.form.sections.membership')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date_of_join">{t('member.form.labels.dateOfJoin')}</label>
              <input
                type="date"
                id="date_of_join"
                name="date_of_join"
                className="form-control"
                value={formData.date_of_join}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="date_of_transfer_add">{t('member.form.labels.dateOfTransferAdd')}</label>
              <input
                type="date"
                id="date_of_transfer_add"
                name="date_of_transfer_add"
                className="form-control"
                value={formData.date_of_transfer_add}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="married_status_name">{t('member.form.labels.marriedStatus')}</label>
              <select
                id="married_status_name"
                name="married_status_name"
                className="form-control"
                value={formData.married_status_name}
                onChange={handleChange}
              >
                <option value="">{t('member.form.options.maritalSelect')}</option>
                <option value="single">{t('member.form.options.maritalSingle')}</option>
                <option value="married">{t('member.form.options.maritalMarried')}</option>
                <option value="divorced">{t('member.form.options.maritalDivorced')}</option>
                <option value="widowed">{t('member.form.options.maritalWidowed')}</option>
              </select>
            </div>
          </div>

          {/* Faith background first, then baptism */}
          <h3 className="section-title" style={{ marginTop: '1.5rem' }}>{t('member.form.sections.religion')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="religion_name">{t('member.form.labels.religion')}</label>
              <input type="text" id="religion_name" name="religion_name" className="form-control"
                value={formData.religion_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="date_of_believe_god">{t('member.form.labels.believeDate')}</label>
              <input type="date" id="date_of_believe_god" name="date_of_believe_god" className="form-control"
                value={formData.date_of_believe_god} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="ever_church">{t('member.form.labels.everChurch')}</label>
            <input type="text" id="ever_church" name="ever_church" className="form-control"
              value={formData.ever_church} onChange={handleChange} />
          </div>

          <h3 className="section-title" style={{ marginTop: '1.5rem' }}>{t('member.form.sections.baptism')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date_of_baptism">{t('member.form.labels.dateOfBaptism')}</label>
              <input
                type="date"
                id="date_of_baptism"
                name="date_of_baptism"
                className="form-control"
                value={formData.date_of_baptism}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="baptism_church">{t('member.form.labels.baptismChurch')}</label>
              <input
                type="text"
                id="baptism_church"
                name="baptism_church"
                className="form-control"
                value={formData.baptism_church}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="baptism_no">{t('member.form.labels.baptismNo')}</label>
              <input type="text" id="baptism_no" name="baptism_no" className="form-control"
                value={formData.baptism_no} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="baptist_priest">{t('member.form.labels.baptistPriest')}</label>
              <input type="text" id="baptist_priest" name="baptist_priest" className="form-control"
                value={formData.baptist_priest} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="baptism_appointed_date">{t('member.form.labels.baptismAppointment')}</label>
            <input type="text" id="baptism_appointed_date" name="baptism_appointed_date" className="form-control"
              value={formData.baptism_appointed_date} onChange={handleChange} />
          </div>

          {/* Cell Groups */}
          <h3 className="section-title" style={{ marginTop: '1.5rem' }}>{t('member.form.sections.cellGroup')}</h3>
          <p className="form-text text-muted" style={{ marginTop: 0 }}>
            {formData.org_leadership === 'district_pastor'
              ? t('member.form.hints.districtPastorOrg')
              : formData.org_leadership === 'zone_leader'
              ? t('member.form.hints.zoneLeaderOrg')
              : t('member.form.hints.groupAutoOrg')}
          </p>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="groups_ch">{t('member.form.labels.cellGroup1')}</label>
              {formData.org_leadership === 'zone_leader' || formData.org_leadership === 'district_pastor' ? (
                <input type="text" id="groups_ch" className="form-control" value="NA" readOnly />
              ) : (
                <select
                  id="groups_ch"
                  name="groups"
                  className="form-control"
                  value={(formData.groups || '').split(';')[0].trim()}
                  onChange={handleChange}
                >
                  <option value="">{t('member.form.labels.selectGroup')}</option>
                  {groupOptions.map((g) => (
                    <option key={g.id} value={g.name}>{g.name}</option>
                  ))}
                  {(() => {
                    const primary = (formData.groups || '').split(';')[0].trim();
                    if (primary && !groupOptions.some((g) => g.name === primary)) {
                      return <option value={primary}>{primary}</option>;
                    }
                    return null;
                  })()}
                </select>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="zones_ch">{t('member.form.labels.cellGroup2')}</label>
              <input
                type="text"
                id="zones_ch"
                name="zones"
                className="form-control"
                value={formData.org_leadership === 'district_pastor' ? 'NA' : formData.zones}
                readOnly
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="districts_ch">{t('member.form.labels.cellGroup3')}</label>
              <input type="text" id="districts_ch" name="districts" className="form-control" value={formData.districts} readOnly />
            </div>
          </div>

          {/* Service & Ministry */}
          <h3 className="section-title" style={{ marginTop: '1.5rem' }}>{t('member.form.sections.service')}</h3>
          <div className="form-group">
            <label>{t('member.form.labels.servePosts')}</label>
            {isEditMode && id ? (
              <ServePositionsInlineEditor memberId={parseInt(id)} />
            ) : (
              <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>儲存會員後可指定事奉崗位</p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="serve_post_exp_ch">{t('member.form.labels.serveExp')}</label>
            <textarea id="serve_post_exp_ch" name="serve_post_exp" className="form-control" rows="2" value={formData.serve_post_exp} onChange={handleChange} />
          </div>

          {/* Emergency Contact */}
          <h3 className="section-title" style={{ marginTop: '1.5rem' }}>{t('member.form.sections.emergency')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="emergency_contact_person">{t('member.form.labels.emergencyPerson')}</label>
              <input
                type="text"
                id="emergency_contact_person"
                name="emergency_contact_person"
                className="form-control"
                value={formData.emergency_contact_person}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="emergency_contact_tel">{t('member.form.labels.emergencyTel')}</label>
              <input
                type="tel"
                id="emergency_contact_tel"
                name="emergency_contact_tel"
                className="form-control"
                value={formData.emergency_contact_tel}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="emergency_contact_relation_name">{t('member.form.labels.emergencyRelation')}</label>
            <input
              type="text"
              id="emergency_contact_relation_name"
              name="emergency_contact_relation_name"
              className="form-control"
              value={formData.emergency_contact_relation_name}
              onChange={handleChange}
              placeholder={t('member.form.placeholders.emergencyRelation')}
            />
          </div>
        </div>
        )}

        {/* ── 家庭狀況 ─────────────────────────── */}
        {activeTab === 'family' && (
        <div className="form-section">
          <h3 className="section-title">{t('member.form.sections.family')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date_of_married">{t('member.form.labels.dateOfMarried')}</label>
              <input type="date" id="date_of_married" name="date_of_married" className="form-control"
                value={formData.date_of_married} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="child_qty">{t('member.form.labels.childQty')}</label>
              <input type="number" id="child_qty" name="child_qty" className="form-control"
                value={formData.child_qty} onChange={handleChange} min="0" />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="native_place">{t('member.form.labels.nativePlace')}</label>
            <input type="text" id="native_place" name="native_place" className="form-control"
              value={formData.native_place} onChange={handleChange} />
          </div>

          {/* Family Members relationship table */}
          <div className="section-header-row" style={{ marginTop: '1.5rem' }}>
            <h3 className="section-title" style={{ margin: 0 }}>
              <i className="fas fa-sitemap" style={{ marginRight: 6 }}></i>家庭成員關係
            </h3>
            <button type="button" className="btn btn-sm btn-outline-primary"
              onClick={() => setFamilyMembers(prev => [...prev, { ...EMPTY_FAMILY_ROW }])}>
              <i className="fas fa-plus"></i> 新增成員
            </button>
          </div>

          {familyMembers.length === 0 ? (
            <p className="text-muted">尚未新增家庭成員。</p>
          ) : (
            <table className="table table-sm family-table">
              <thead>
                <tr>
                  <th>關係</th>
                  <th>中文姓名</th>
                  <th>本教會</th>
                  <th>敬拜號</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {familyMembers.map((f, i) => (
                  <FamilyMemberRow
                    key={i}
                    f={f}
                    idx={i}
                    onUpdate={(idx, field, value) =>
                      setFamilyMembers(prev => prev.map((m, mi) => mi === idx ? { ...m, [field]: value } : m))
                    }
                    onRemove={(idx) =>
                      setFamilyMembers(prev => prev.filter((_, mi) => mi !== idx))
                    }
                  />
                ))}
              </tbody>
            </table>
          )}
          <p style={{ color: '#6b7280', fontSize: '11px', marginTop: 6 }}>
            <i className="fas fa-info-circle" style={{ marginRight: 4 }}></i>
            勾選「本教會」並選擇敬拜號後，儲存時將自動在對方的家庭成員中新增反向關係（成人及兒童/少年均支援）。
          </p>
        </div>
        )}

        {/* ── 進階資料 ─────────────────────────── */}
        {activeTab === 'advanced' && (
        <div className="form-section">
          <h3 className="section-title">{t('member.form.sections.education')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="edu_grade_name">{t('member.form.labels.eduGrade')}</label>
              <input type="text" id="edu_grade_name" name="edu_grade_name" className="form-control"
                value={formData.edu_grade_name} onChange={handleChange}
                placeholder={t('member.form.placeholders.eduGrade')} />
            </div>
            <div className="form-group">
              <label htmlFor="edu_name">{t('member.form.labels.eduName')}</label>
              <input type="text" id="edu_name" name="edu_name" className="form-control"
                value={formData.edu_name} onChange={handleChange}
                placeholder={t('member.form.placeholders.eduName')} />
            </div>
          </div>

          <h3 className="section-title" style={{ marginTop: '1.5rem' }}>{t('member.form.sections.occupation')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="job">{t('member.form.labels.job')}</label>
              <input type="text" id="job" name="job" className="form-control"
                value={formData.job} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label htmlFor="income_name">{t('member.form.labels.income')}</label>
              <input type="text" id="income_name" name="income_name" className="form-control"
                value={formData.income_name} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="work_region_name">{t('member.form.labels.workRegion')}</label>
              <input type="text" id="work_region_name" name="work_region_name" className="form-control"
                value={formData.work_region_name} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="pro_qual_items">{t('member.form.labels.proQual')}</label>
            <textarea id="pro_qual_items" name="pro_qual_items" className="form-control" rows="2"
              value={formData.pro_qual_items} onChange={handleChange} />
          </div>

          <h3 className="section-title" style={{ marginTop: '1.5rem' }}>{t('member.form.sections.health')}</h3>
          <div className="form-group">
            <label htmlFor="health">{t('member.form.labels.health')}</label>
            <textarea id="health" name="health" className="form-control" rows="2"
              value={formData.health} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="health_items_name">{t('member.form.labels.healthItems')}</label>
            <textarea id="health_items_name" name="health_items_name" className="form-control" rows="2"
              value={formData.health_items_name} onChange={handleChange} />
          </div>
        </div>
        )}

        {/* ── 備註 ─────────────────────────────── */}
        {activeTab === 'remarks' && (
        <div className="form-section">
          <h3 className="section-title">{t('member.form.sections.remarks')}</h3>
          <div className="form-group">
            <label htmlFor="remark">{t('member.form.labels.remark')}</label>
            <textarea id="remark" name="remark" className="form-control" rows="4"
              value={formData.remark} onChange={handleChange}
              placeholder={t('member.form.placeholders.remark')} />
          </div>
          <div className="form-group">
            <label htmlFor="remark2">{t('member.form.labels.remark2')}</label>
            <textarea id="remark2" name="remark2" className="form-control" rows="3"
              value={formData.remark2} onChange={handleChange} />
          </div>
        </div>
        )}

        </div> {/* end form-body */}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={submitting}
          >
            {t('member.form.cancel')}
          </button>
          
          {/* Change Member Type button - show for all members in edit mode */}
          {isEditMode && memberData && (
            <button
              type="button"
              className="btn btn-success"
              onClick={handleUpgradeClick}
              disabled={submitting}
            >
              <i className="fas fa-exchange-alt"></i> {t('member.upgrade.button', { defaultValue: 'Change Member Type' })}
            </button>
          )}
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                {t('member.form.saving')}
              </>
            ) : (
              isEditMode ? t('member.form.updateAction') : t('member.form.createAction')
            )}
          </button>
        </div>
      </form>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('member.upgrade.title', { name: formData.chi_name, defaultValue: `Upgrade Member: ${formData.chi_name}` })}</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowUpgradeModal(false)}
                disabled={upgradeLoading}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="upgrade_member_type">{t('member.upgrade.memberType', { defaultValue: 'Member Type' })}</label>
                <select
                  id="upgrade_member_type"
                  name="member_type"
                  className="form-control"
                  value={upgradeData.member_type}
                  onChange={handleUpgradeChange}
                  disabled={upgradeLoading}
                >
                  <option value="regular">{t('member.tabs.fullMember', { defaultValue: 'Full Member' })}</option>
                  <option value="associate">{t('member.tabs.associate', { defaultValue: 'Associate Member' })}</option>
                  <option value="temporary">{t('member.tabs.temporary', { defaultValue: 'Temporary Member' })}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="upgrade_worship_id">
                  {t('member.upgrade.worshipId', { defaultValue: 'Worship Number' })}
                  {upgradeData.member_type !== 'temporary' && <span className="text-danger"> *</span>}
                </label>
                <input
                  type="text"
                  id="upgrade_worship_id"
                  name="worship_id"
                  className="form-control"
                  value={upgradeData.worship_id}
                  onChange={handleUpgradeChange}
                  placeholder={upgradeData.member_type === 'temporary' ? 'T000001' : '000001'}
                  maxLength={7}
                  pattern="^\d{6}$|^T\d{6}$"
                  disabled={upgradeLoading}
                  required={upgradeData.member_type !== 'temporary'}
                />
                <small className="form-text text-muted">
                  {upgradeData.member_type === 'temporary'
                    ? 'T+6位數字 (e.g. T000001)'
                    : '6位數字 (e.g. 000001)'}
                </small>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowUpgradeModal(false)}
                disabled={upgradeLoading}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpgradeMember}
                disabled={upgradeLoading}
              >
                {upgradeLoading ? t('member.upgrade.upgrading', { defaultValue: 'Updating...' }) : t('member.upgrade.confirm', { defaultValue: 'Confirm' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberForm;
